import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';
import styled, { keyframes } from 'styled-components';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const FAQContainer = styled.div`
  padding: 140px 0 60px;
  
  @media (max-width: 768px) {
    padding: 100px 0 40px;
  }
  background: #ffffff;
  min-height: 100vh;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 5%;
`;

const PageTitle = styled.h1`
  text-align: center;
  font-size: 4.5rem;
  color: #2c2c2c;
  margin-bottom: 30px;
  
  letter-spacing: -2px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: linear-gradient(90deg, #0a1628, #1a2744);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const PageSubtitle = styled.p`
  text-align: center;
  font-size: 1.4rem;
  color: #6c757d;
  margin-bottom: 120px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.7;
  
`;

// Hero Section
const HeroSection = styled.section`
  position: relative;
  height: 500px;
  border-radius: 25px;
  overflow: hidden;
  margin-bottom: 120px;
  
  &.animate-in {
    animation: ${scaleIn} 0.8s ease-out;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('https://images.unsplash.com/photo-1541746972996-4e0b0f93e586?ixlib=rb-4.0.3&auto=format&fit=crop&w=2126&q=80') no-repeat center center;
    background-size: cover;
    transition: transform 0.5s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(10, 22, 40, 0.85), rgba(26, 39, 68, 0.7));
  }
  
  &:hover::before {
    transform: scale(1.05);
  }
`;

const HeroContent = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  z-index: 2;
  color: white;
  
  h2 {
    font-size: 3.5rem;
    
    margin-bottom: 25px;
    letter-spacing: -1.5px;
  
    color: #ffffff;}
  
  p {
    font-size: 1.3rem;
    
    line-height: 1.6;
    max-width: 600px;
    margin: 0 auto;
    opacity: 0.95;
  
    color: #ffffff;}
  
  @media (max-width: 768px) {
    h2 {
      font-size: 2.5rem;
    }
    
    p {
      font-size: 1.1rem;
    }
  }
`;

// Categories Section
const CategoriesSection = styled.section`
  margin-bottom: 120px;
  
  &.animate-in {
    .categories-grid .category-card:nth-child(1) { animation: ${scaleIn} 0.6s ease-out 0.1s both; }
    .categories-grid .category-card:nth-child(2) { animation: ${scaleIn} 0.6s ease-out 0.3s both; }
    .categories-grid .category-card:nth-child(3) { animation: ${scaleIn} 0.6s ease-out 0.5s both; }
    .categories-grid .category-card:nth-child(4) { animation: ${scaleIn} 0.6s ease-out 0.7s both; }
  }
`;

const SectionTitle = styled.h2`
  font-size: 3.5rem;
  
  color: #2c2c2c;
  margin-bottom: 80px;
  text-align: center;
  letter-spacing: -1.5px;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  margin-bottom: 80px;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CategoryCard = styled.button`
  background: ${props => props.active ? 'linear-gradient(135deg, #0a1628, #1a2744)' : 'white'};
  color: ${props => props.active ? 'white' : '#2c2c2c'};
  border: 2px solid ${props => props.active ? 'transparent' : 'rgba(10, 22, 40, 0.1)'};
  padding: 30px 20px;
  border-radius: 20px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.4s ease;
  font-size: 1rem;
  text-align: center;
  
  &:hover {
    transform: translateY(-5px);
    border-color: ${props => props.active ? 'transparent' : '#0a1628'};
    box-shadow: 0 15px 30px rgba(10, 22, 40, 0.15);
  }
  
  .icon {
    font-size: 2.5rem;
    margin-bottom: 15px;
    display: block;
  }
  
  .count {
    font-size: 0.9rem;
    opacity: 0.8;
    margin-top: 5px;
  }
`;

// FAQ Items
const FAQSection = styled.section`
  margin-bottom: 120px;
  
  &.animate-in {
    .faq-list .faq-item:nth-child(1) { animation: ${fadeInUp} 0.6s ease-out 0.1s both; }
    .faq-list .faq-item:nth-child(2) { animation: ${fadeInUp} 0.6s ease-out 0.2s both; }
    .faq-list .faq-item:nth-child(3) { animation: ${fadeInUp} 0.6s ease-out 0.3s both; }
    .faq-list .faq-item:nth-child(4) { animation: ${fadeInUp} 0.6s ease-out 0.4s both; }
    .faq-list .faq-item:nth-child(5) { animation: ${fadeInUp} 0.6s ease-out 0.5s both; }
    .faq-list .faq-item:nth-child(6) { animation: ${fadeInUp} 0.6s ease-out 0.6s both; }
  }
`;

const FAQList = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const FAQItem = styled.div`
  background: white;
  border-radius: 20px;
  margin-bottom: 25px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(10, 22, 40, 0.05);
  transition: all 0.4s ease;
  overflow: hidden;
  
  &:hover {
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.12);
    border-color: rgba(10, 22, 40, 0.15);
  }
`;

const FAQQuestion = styled.button`
  width: 100%;
  padding: 30px 35px;
  background: ${props => props.isOpen ? 'linear-gradient(135deg, #0a1628, #1a2744)' : 'white'};
  color: ${props => props.isOpen ? 'white' : '#2c2c2c'};
  border: none;
  text-align: left;
  font-size: 1.3rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.4s ease;
  letter-spacing: -0.5px;
  
  &:hover {
    background: ${props => props.isOpen ? 'linear-gradient(135deg, #050a14, #0a1628)' : 'rgba(10, 22, 40, 0.05)'};
  }
  
  .icon {
    font-size: 1.5rem;
    transition: all 0.4s ease;
    transform: ${props => props.isOpen ? 'rotate(45deg)' : 'rotate(0deg)'};
    color: ${props => props.isOpen ? 'white' : '#0a1628'};
  }
`;

const FAQAnswer = styled.div`
  padding: ${props => props.isOpen ? '30px 35px' : '0 35px'};
  max-height: ${props => props.isOpen ? '500px' : '0'};
  overflow: hidden;
  transition: all 0.4s ease;
  background: #f8f9fa;
  
  p {
    color: #6c757d;
    line-height: 1.8;
    margin-bottom: 20px;
    font-size: 1.1rem;
    
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  ul {
    margin: 20px 0;
    padding-left: 0;
    list-style: none;
    
    li {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      margin-bottom: 10px;
      color: #6c757d;
      font-size: 1rem;
      line-height: 1.6;
      
      &::before {
        content: '•';
        color: #0a1628;
        font-weight: bold;
        margin-top: 2px;
      }
    }
  }
  
  .highlight {
    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
    padding: 20px;
    border-radius: 12px;
    margin: 20px 0;
    border-left: 4px solid #0a1628;
    
    p {
      margin: 0;
      font-weight: 500;
      color: #1565c0;
    }
  }
`;

// Contact CTA Section
const ContactCTASection = styled.section`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 80px 0;
  border-radius: 30px;
  text-align: center;
  
  &.animate-in {
    animation: ${fadeInUp} 0.8s ease-out;
  }
  
  h2 {
    font-size: 3rem;
    
    color: #2c2c2c;
    margin-bottom: 25px;
    letter-spacing: -1.5px;
  }
  
  p {
    font-size: 1.3rem;
    color: #6c757d;
    line-height: 1.7;
    max-width: 600px;
    margin: 0 auto 40px;
    
  }
  
  .contact-btn {
    background: linear-gradient(135deg, #0a1628, #1a2744);
    color: white;
    padding: 18px 45px;
    border: none;
    border-radius: 30px;
    font-weight: 600;
    font-size: 1.1rem;
    cursor: pointer;
    transition: all 0.3s ease;
    text-decoration: none;
    display: inline-block;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    
    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 15px 35px rgba(10, 22, 40, 0.3);
    }
  }
  
  @media (max-width: 768px) {
    h2 {
      font-size: 2.2rem;
    }
    
    p {
      font-size: 1.1rem;
    }
  }
`;

const FAQ = () => {
  const [activeCategory, setActiveCategory] = useState('genel');
  const [openItems, setOpenItems] = useState(new Set());
  const [animatedSections, setAnimatedSections] = useState(new Set());
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimatedSections(prev => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const toggleItem = (id) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(id)) {
      newOpenItems.delete(id);
    } else {
      newOpenItems.add(id);
    }
    setOpenItems(newOpenItems);
  };

  const categories = [
    { id: 'genel', name: t('faq.cat1'), icon: '❓', count: 6 },
    { id: 'urun', name: t('faq.cat2'), icon: '🏗️', count: 5 },
    { id: 'teslimat', name: t('faq.cat3'), icon: '🚚', count: 4 },
    { id: 'teknik', name: t('faq.cat4'), icon: '🔧', count: 7 }
  ];

  const faqDataLocal = {
    genel: [
      {
        id: 'g1',
        question: t('faqData.genel_q1'),
        answer: t('faqData.genel_a1')
      },
      {
        id: 'g2',
        question: t('faqData.genel_q2'),
        answer: t('faqData.genel_a2')
      },
      {
        id: 'g3',
        question: t('faqData.genel_q3'),
        answer: t('faqData.genel_a3')
      }
    ],
    urun: [
      {
        id: 'u1',
        question: t('faqData.urun_q1'),
        answer: t('faqData.urun_a1')
      },
      {
        id: 'u2',
        question: t('faqData.urun_q2'),
        answer: t('faqData.urun_a2')
      }
    ],
    teslimat: [
      {
        id: 't1',
        question: t('faqData.teslimat_q1'),
        answer: t('faqData.teslimat_a1')
      }
    ],
    teknik: [
      {
        id: 'te1',
        question: t('faqData.teknik_q1'),
        answer: t('faqData.teknik_a1')
      }
    ]
  };

  const currentFAQs = faqDataLocal[activeCategory] || faqDataLocal.genel;

  return (
    <FAQContainer>
      <Container>
        <PageTitle>{t('faq.title')}</PageTitle>
        <PageSubtitle>
          {t('faq.subtitle')}
        </PageSubtitle>

        <HeroSection 
          id="hero-section" 
          data-animate
          className={animatedSections.has('hero-section') ? 'animate-in' : ''}
        >
          <HeroContent>
            <h2>{t('faq.heroTitle')}</h2>
            <p>
              {t('faq.heroDesc')}
            </p>
          </HeroContent>
        </HeroSection>

        <CategoriesSection 
          id="categories-section" 
          data-animate
          className={animatedSections.has('categories-section') ? 'animate-in' : ''}
        >
          <SectionTitle>{t('faq.categorySelect')}</SectionTitle>
          <CategoriesGrid className="categories-grid">
            {categories.map(category => (
              <CategoryCard
                key={category.id}
                active={activeCategory === category.id}
                onClick={() => setActiveCategory(category.id)}
                className="category-card"
              >
                <span className="icon">{category.icon}</span>
                <div>{category.name}</div>
                <div className="count">{category.count} {t('faq.questionsCount')}</div>
              </CategoryCard>
            ))}
          </CategoriesGrid>
        </CategoriesSection>

        <FAQSection 
          id="faq-section" 
          data-animate
          className={animatedSections.has('faq-section') ? 'animate-in' : ''}
        >
          <FAQList className="faq-list">
            {currentFAQs.map((faq) => (
              <FAQItem key={faq.id} className="faq-item">
                <FAQQuestion
                  isOpen={openItems.has(faq.id)}
                  onClick={() => toggleItem(faq.id)}
                >
                  {faq.question}
                  <span className="icon">+</span>
                </FAQQuestion>
                <FAQAnswer isOpen={openItems.has(faq.id)}>
                  <p>{faq.answer}</p>
                </FAQAnswer>
              </FAQItem>
            ))}
          </FAQList>
        </FAQSection>

        <ContactCTASection 
          id="contact-cta-section" 
          data-animate
          className={animatedSections.has('contact-cta-section') ? 'animate-in' : ''}
        >
          <h2>{t('faq.notFoundTitle')}</h2>
          <p>
            {t('faq.notFoundDesc')}
          </p>
          <a href="/iletisim" className="contact-btn">
            {t('faq.contactBtn')}
          </a>
        </ContactCTASection>
      </Container>
    </FAQContainer>
  );
};

export default FAQ;